/**
 * Header Component
 * 
 * Top navigation bar with ship selector, notifications, sync status, and user menu
 * Fully functional with dropdown menus
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  Bell,
  User,
  Settings,
  LogOut,
  ChevronDown,
  Ship,
  Check,
  AlertCircle,
  Database,
  HardDrive,
  Monitor,
  MonitorDot,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useVessel } from '@/contexts/VesselContext';
import { SyncStatusIndicator } from '@/components/features/header/SyncStatusIndicator';
import { createLogger } from '@/lib/utils/logger';

const logger = createLogger('Header');

interface HeaderProps {
  onMenuClick: () => void;
  sidebarCollapsed: boolean;
}

export default function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const { currentUser: authUser, logout } = useAuth();
  const { dataSource, toggleDataSource } = useDataSource();
  const { toggleUITheme, isClassic } = useTheme();
  const { currentVessel, setCurrentVessel, vessels } = useVessel();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [shipMenuOpen, setShipMenuOpen] = useState(false);
  
  const notificationsRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const shipMenuRef = useRef<HTMLDivElement>(null);

  // Mock data (will be replaced with real data in Phase 3)
  const unreadNotifications = 3;

  const handleVesselChange = (vesselId: string) => {
    const vessel = vessels.find(v => v.id === vesselId);
    if (vessel) {
      setCurrentVessel(vessel);
      setShipMenuOpen(false);
    }
  };
  
  const displayUser = {
    name: authUser 
      ? (authUser.firstName && authUser.lastName 
        ? `${authUser.firstName} ${authUser.lastName}`
        : authUser.user_metadata?.full_name 
        || authUser.email?.split('@')[0] 
        || 'User')
      : 'User',
    role: authUser?.role ? authUser.role.replace('_', ' ').toUpperCase() : 'User',
    avatar: null,
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (shipMenuRef.current && !shipMenuRef.current.contains(event.target as Node)) {
        setShipMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      logger.error('Logout failed', error);
    }
  };

  return (
    <header className={isClassic ? 'classic-header flex h-16 items-center justify-between px-4' : 'flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 shadow-sm'}>
      {/* Left section */}
      <div className="flex items-center space-x-4">
        {/* Mobile menu button */}
        <button
          onClick={onMenuClick}
          className="rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900 md:hidden"
        >
          <Menu className="h-6 w-6" />
        </button>

        {/* Ship selector */}
        <div className="relative" ref={shipMenuRef}>
          <button
            onClick={() => setShipMenuOpen(!shipMenuOpen)}
            className="flex items-center space-x-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Ship className="h-4 w-4 text-blue-600" />
            <span className="hidden sm:inline">{currentVessel?.name || 'Select Vessel'}</span>
            <ChevronDown className="h-4 w-4" />
          </button>

          {/* Ship dropdown */}
          {shipMenuOpen && (
            <div className="absolute left-0 mt-2 w-64 rounded-lg border-2 border-gray-300 bg-white shadow-xl z-50 overflow-hidden">
              <div className="bg-gray-100 border-b border-gray-300 px-4 py-2.5">
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Select Vessel
                </div>
              </div>
              <div className="p-2">
                {vessels.map((vessel) => (
                  <button
                    key={vessel.id}
                    onClick={() => handleVesselChange(vessel.id)}
                    className={`flex w-full items-center space-x-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
                      vessel.id === currentVessel?.id
                        ? 'bg-blue-50 text-blue-700 border border-blue-200 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Ship className={`h-4 w-4 flex-shrink-0 ${vessel.id === currentVessel?.id ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span className="flex-1 text-left">{vessel.name}</span>
                    {vessel.id === currentVessel?.id && <Check className="h-4 w-4 flex-shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right section */}
      <div className="flex items-center space-x-4">
          {/* UI Theme Toggle */}
          <button
            onClick={toggleUITheme}
            className={isClassic ? "classic-btn flex items-center gap-2 px-3 py-1.5" : "flex items-center space-x-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50 transition-colors"}
            title={`Currently: ${isClassic ? 'Classic' : 'Modern'} UI - Click to switch`}
          >
            {isClassic ? (
              <>
                <MonitorDot className="h-4 w-4" />
                <span className="hidden lg:inline text-xs">Classic</span>
              </>
            ) : (
              <>
                <Monitor className="h-4 w-4 text-purple-600" />
                <span className="hidden lg:inline text-purple-700">Modern</span>
              </>
            )}
          </button>


        {/* Data Source Toggle */}
        <button
          onClick={toggleDataSource}
          className="flex items-center space-x-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium hover:bg-gray-50 transition-colors"
          title={`Currently using ${dataSource === 'supabase' ? 'Supabase' : 'Dexie (Local)'} - Click to switch`}
        >
          {dataSource === 'supabase' ? (
            <>
              <Database className="h-4 w-4 text-green-600" />
              <span className="hidden lg:inline text-green-700">Supabase</span>
            </>
          ) : (
            <>
              <HardDrive className="h-4 w-4 text-blue-600" />
              <span className="hidden lg:inline text-blue-700">Dexie</span>
            </>
          )}
        </button>

        {/* Sync status - Real-time sync indicator with manual trigger */}
        <SyncStatusIndicator />

        {/* Notifications */}
        <div className="relative" ref={notificationsRef}>
          <button
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="relative rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          >
            <Bell className="h-5 w-5" />
            {unreadNotifications > 0 && (
              <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                {unreadNotifications}
              </span>
            )}
          </button>

          {/* Notifications dropdown */}
          {notificationsOpen && (
            <div className="absolute right-0 mt-2 w-80 rounded-lg border-2 border-gray-300 bg-white shadow-xl z-50 overflow-hidden">
              <div className="bg-gray-100 border-b border-gray-300 px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <div className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        Certificate Expiring Soon
                      </p>
                      <p className="text-sm text-gray-500">
                        IOPP Certificate expires in 15 days
                      </p>
                      <p className="text-xs text-gray-400 mt-1">2 hours ago</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <Check className="h-5 w-5 text-green-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        Maintenance Completed
                      </p>
                      <p className="text-sm text-gray-500">
                        Main engine service completed successfully
                      </p>
                      <p className="text-xs text-gray-400 mt-1">5 hours ago</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 hover:bg-gray-50 cursor-pointer">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        Low Stock Alert
                      </p>
                      <p className="text-sm text-gray-500">
                        Engine oil below reorder point
                      </p>
                      <p className="text-xs text-gray-400 mt-1">1 day ago</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 border-t border-gray-300 p-3">
                <button
                  onClick={() => {
                    navigate('/notifications');
                    setNotificationsOpen(false);
                  }}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  View all notifications
                </button>
              </div>
            </div>
          )}
        </div>

        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center space-x-2 rounded-md p-2 hover:bg-gray-100"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-white">
              {displayUser.avatar ? (
                <img src={displayUser.avatar} alt="" className="h-8 w-8 rounded-full" />
              ) : (
                <User className="h-5 w-5" />
              )}
            </div>
            <div className="hidden md:block text-left">
              <div className="text-sm font-medium text-gray-900">{displayUser.name}</div>
              <div className="text-xs text-gray-500">{displayUser.role}</div>
            </div>
            <ChevronDown className="h-4 w-4 text-gray-500" />
          </button>

          {/* User dropdown */}
          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-lg border-2 border-gray-300 bg-white shadow-xl z-50 overflow-hidden">
              <div className="bg-gray-100 border-b border-gray-300 px-4 py-3">
                <div className="text-sm font-medium text-gray-900">{displayUser.name}</div>
                <div className="text-xs text-gray-600">{displayUser.role}</div>
              </div>
              <div className="p-2">
                <button
                  onClick={() => {
                    navigate('/profile');
                    setUserMenuOpen(false);
                  }}
                  className="flex w-full items-center space-x-2 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <User className="h-4 w-4" />
                  <span>Profile</span>
                </button>
                <button
                  onClick={() => {
                    navigate('/settings');
                    setUserMenuOpen(false);
                  }}
                  className="flex w-full items-center space-x-2 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Settings className="h-4 w-4" />
                  <span>Settings</span>
                </button>
              </div>
              <div className="bg-gray-50 border-t border-gray-300 p-2">
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center space-x-2 rounded-md px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

